import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { defer } from '/imports/utils';
import { InputProps, InputStyled } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';

const NumberInputStyled = styled(InputStyled)`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    appearance: inherit;
    -moz-appearance: textfield;
  }
`;

export interface NumberInputProps extends InputProps<number> {
  className?: string;
  placeholder?: string;
  int?: boolean;
  disabled?: boolean;
  step?: string;
  min?: string;
  max?: string;
  unitPrefix?: string;
  decimals?: number;  // fixed-decimal DISPLAY (e.g. currency = 2 → 26395.8 shows as 26395.80)
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  defaultStrValue?: string;
}

/** Numeric input that parses/clamps its value and reports valid numbers via onChange. */
export const NumberInput = (props: NumberInputProps) => {
  const defaultStrValue = props.defaultStrValue || '';
  // Fixed-decimal display: applied to the SHOWN string only (mount + blur), never while typing.
  const fmt = (v: number) => (props.decimals != null ? v.toFixed(props.decimals) : `${v}`);
  const [strValue, setStrValue] = useState(props.value !== undefined ? fmt(props.value) : defaultStrValue);
  const [isValid, setIsValid] = useState(true);
  // The last value this input itself reported upward. The shown string is local state so that typing is
  // not reformatted mid-keystroke, but that left `value` read only at mount: a parent setting a new one
  // changed its own state and nothing on screen. Comparing against what we reported tells the two apart —
  // our own change echoes back and is ignored, anything else is an external set and is shown.
  const reported = useRef<number | undefined>(props.value);
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  useEffect(() => {
    if (props.value === reported.current) return;   // our own edit coming back round
    reported.current = props.value;
    setStrValue(props.value !== undefined ? fmt(props.value) : defaultStrValue);
  }, [props.value]);
  const clearInput = () => {
    reported.current = undefined;
    props.onChange?.(undefined);
    defer(() => props.onClear?.());
    defer(() => setStrValue(defaultStrValue));
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = props.int ? parseInt(e.target.value) : parseFloat(e.target.value);
    const isNum = !isNaN(value);
    if (isNum && props.min) value = Math.max(parseFloat(props.min), value);
    if (isNum && props.max) value = Math.min(parseFloat(props.max), value);
    setStrValue(`${e.target.value}`);
    if (isNum) {
      reported.current = value;
      props.onChange?.(value, e);
    }
    setIsValid(isNum);
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (isValid) {
      // Snap the shown value to the fixed decimals on blur (not per keystroke): 26395.8 → 26395.80.
      if (props.decimals != null && props.value !== undefined && strValue.trim() !== '') setStrValue(fmt(props.value));
      return;
    }
    setStrValue(props.value !== undefined ? fmt(props.value) : defaultStrValue);
    props.onChange?.(props.value);
    props.onBlur?.(e);
  };
  return (
    <NumberInputStyled id={props.id} className={`input number-input ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {hasStartIcon && <Icon className="icon-start" icon={props.icon as any} />}
      <input
        id={props.inputId}
        className={hasStartIcon ? 'has-start-icon' : ''}
        type="number"
        step={props.step || ''}
        min={props.min}
        max={props.max}
        placeholder={props.placeholder || ''}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        value={strValue}
        onChange={(e) => onChange(e)}
        onClick={(e) => props.onClick?.(e)}
        onFocus={props.onFocus}
        onBlur={(e) => onBlur(e)}
        onKeyUp={(e) => {
          props.onKeyPress?.(e);
          if (e.key === 'Enter') defer(() => props.onEnter?.(props.value, e));
        }}
        autoComplete={props.autoComplete}
        required={props.required}
      />
      {
        props.value !== undefined &&
        !props.disabled &&
        !(props.type === 'color') &&
        <Icon className="clear" icon="cancel" onClick={() => clearInput()} /> || null
      }
    </NumberInputStyled>
  );
};